import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAlert } from '../Components/AlertContext';
import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import Table from '../Components/Tables';
import { UsersApiProvider, useUsersApi } from '../Services/UsersApiProvider';
import { formatDateUTC } from '../utils/formatDate';
import { UserApi } from '../Services/UsersApiProviderTypes';

function UsersHub({ canEdit }) {
  const { organizationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();
  const { getUsers, deleteUser } = useUsersApi();

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [alertShown, setAlertShown] = useState<boolean>(false);
  const [users, setUsers] = useState<UserApi[]>([]);
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);

  useEffect(() => {
    if (location.state?.successMessage && !alertShown) {
      showAlert('success', location.state.successMessage);
      setAlertShown(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.successMessage, alertShown, navigate, location.pathname, showAlert]);

  const columns = [
    { name: 'ID', selector: (row: UserApi) => row.id, sortable: false },
    { name: 'Name', selector: (row: UserApi) => row.name, sortable: false },
    { name: 'Email', selector: (row: UserApi) => row.email, sortable: false },
    {
      name: 'Status',
      selector: (row: UserApi) => (row.is_active ? 'Active' : 'Inactive'),
      sortable: false,
    },
    { name: 'Created', selector: (row: UserApi) => formatDateUTC(row.created_at), sortable: false },
    { name: 'Updated', selector: (row: UserApi) => formatDateUTC(row.updated_at), sortable: false },
  ];

  const handleRowClicked = (row: UserApi) => {
    navigate(`/organizations/${organizationId}/users-hub/${row.id}`, {
      state: { organizationId },
    });
  };

  const handleDelete = async (selectedId: string[]) => {
    if (!selectedId) return;

    showConfirmation(`Are you sure you want to delete this user?`, async () => {
      try {
        setLoading(true);
        await deleteUser(selectedId[0]);
        await fetchUsers(0, itemsPerPage);
        showAlert('success', 'User deleted successfully!');
      } catch (error) {
        showAlert('fail', 'Failed to delete user!');
      } finally {
        setLoading(false);
      }
    });
  };

  const fetchUsers = async (skip: number, limit: number): Promise<void> => {
    if (limit !== itemsPerPage) {
      setItemsPerPage(limit);
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getUsers(skip, limit);
      setUsers(response.items);
      setTotalUsers(response.totalCount);
    } catch (error) {
      setError(error.message);
      showAlert('fail', 'Failed to fetch users!');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(0, itemsPerPage);
  }, []);

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-12">
          {error ? (
            <div>Error: {error}</div>
          ) : (
            <>
              <Table
                title="Users"
                columns={columns}
                data={users}
                selectableRows
                selectableRowsHighlight
                paginationPerPage={itemsPerPage}
                progressPending={loading}
                paginationTotalRows={totalUsers}
                onRowClicked={handleRowClicked}
                sortIcon={<i className="fa-solid fa-chevron-down"></i>}
                onDelete={handleDelete}
                onChangePage={fetchUsers}
              />
              {canEdit && (
                <Button
                  background="color2"
                  size="large"
                  onClick={() => navigate(`/organizations/${organizationId}/users-hub/new-user`)}
                >
                  Create New User
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function UsersHubWrapper({ canEdit }) {
  return (
    <ConfirmationProvider>
      <UsersApiProvider>
        <UsersHub canEdit={canEdit} />
      </UsersApiProvider>
    </ConfirmationProvider>
  );
}

export default UsersHubWrapper;
